"use client";
import { LyricsWithSlug } from "~/types";

import InsightsIcon from '@mui/icons-material/Insights';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "~/components/Link";
import Markdown from "~/components/Markdown";
import ReportButton from "./ReportButton";
import VersuriBreadcrumbs from "./VersuriBreadcrumbs";
import VersuriFontSizeControls from "./VersuriFontSizeControls";
import LyricKeywordChipLink from "./VersuriKeywordChipLink";
import VersuriLyrics from "./VersuriLyrics";
import YoutubePlayer from "./YoutubePlayer";

interface VersuriDisplayProps {
  lyrics: LyricsWithSlug;
}

export default function VersuriDisplay({ lyrics }: VersuriDisplayProps) {
  return (
    <Box>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header with Title and Font Size Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: "row",
            mb: 3,
            gap: 2,
          }}
        >
          <Box>
            <VersuriBreadcrumbs categories={lyrics.categories} />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                // color: "primary.main",
                textShadow: "0 1px 3px color-mix(in srgb, var(--mui-palette-background-default), var(--mui-palette-background-default) 70%)",
              }}
            >
              {lyrics.name}
            </Typography>
            <Typography
              component="h2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
                textShadow: "0 1px 3px color-mix(in srgb, var(--mui-palette-background-default), var(--mui-palette-background-default) 70%)",
              }}
            >
              {lyrics.artist}
            </Typography>
          </Box>
          <VersuriFontSizeControls />
        </Box>

        {/* YouTube embed (if available) */}
        {lyrics.youtube?.id && (
          <YoutubePlayer lyrics={lyrics} />
        )}

        {/* Lyrics Content */}
        <VersuriLyrics lyrics={lyrics} />
      </Box>

      <Box sx={{
        position: "relative",
        mt: 3, p: 2,
        bgcolor: "background.paper",
        color: "text.secondary",
        border: "1px solid",
        borderColor: "divider",
        fontSize: "0.95rem",
        borderLeftWidth: { xs: 0, sm: 1 },
        borderRightWidth: { xs: 0, sm: 1 },
      }}>
        {lyrics.pageIntro ? (
          <Typography
            component="p"
            sx={{
              fontSize: "0.95rem",
              mb: 1,
            }}
          >
            <InsightsIcon fontSize="small" sx={{ verticalAlign: "top", mr: 0.5 }} />
            {lyrics.pageIntro}
          </Typography>
        ) : null}
        {lyrics.pageDetails && (
          <Markdown
            disableEmphasizeHeadings
            disableParagraphMargin
            components={{
              a: ({ href, children }) => <Link href={href || "#"} effect="underline" rel="nofollow">{children}</Link>,
            }}
            sx={{
              fontSize: "inherit",
              color: "text.secondary",
              "& strong": { fontSize: ".9em", color: "text.secondary" },
              "& a": { color: "text.primary", textDecoration: "none", fontWeight: 600 },
              mb: 1,
            }}>
            {lyrics.pageDetails}
          </Markdown>
        )}

        {lyrics.contributorName && (
          <Typography
            sx={{
              fontSize: '.9em',
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Versuri adăugate de {lyrics.contributorName}
          </Typography>
        )}

        <Box sx={{ mb: 2, ml: -1, display: 'flex', gap: 1, alignItems: 'center' }}>
          <ReportButton />
        </Box>

        {/* Song Details - Condensed Single Line */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          {Array.isArray(lyrics.targetAge)
            ? lyrics.targetAge.map((age) => (
              <LyricKeywordChipLink key={age} category={age} type="targetAge" />
            ))
            : lyrics.targetAge
              ? <LyricKeywordChipLink category={lyrics.targetAge} type="targetAge" />
              : null}
          {lyrics.mood
            ? lyrics.mood.split(",").map((mood) => (
              <LyricKeywordChipLink
                key={mood.trim()}
                category={mood.trim().toLowerCase()}
                type="mood"
              />
            ))
            : null}
          {lyrics.categories
            ? lyrics.categories.map((cat) => (
              <LyricKeywordChipLink
                key={cat.trim()}
                category={cat.trim().toLowerCase()}
                type="category"
              />
            ))
            : null}
        </Box>


      </Box>
    </Box>
  );
}
